import type { AgingBasis, ArInvoiceFlatSortBy } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { getInvoiceStatusBadge } from '@/components/accounts-receivable/invoice-status-badge';
import { InvoiceSentCell } from '@/components/accounts-receivable/InvoiceSentCell';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import {
  formatDecimalCurrency,
  formatDate,
} from '@/components/accounts-receivable/ar-format';
import { getDaysOut } from './ar-report-shared';
import type { ArReportState, GroupedSortBy } from './useArReportState';
import { CollectionStatusChip } from '@/components/accounts-receivable/CollectionStatusChip';
import { CustomerCommsPopover } from '@/components/accounts-receivable/CustomerCommsPopover';
import {
  useArCustomerCommsSummary,
  useSetCollectionStatus,
} from '@/api/rpc/hooks/accounts-receivable/useArCustomerComms';
import type { CustomerCollectionStatus } from '@erp/rpc';

// ─── Grouped Table ───────────────────────────────────────────────────────────

interface GroupedTableProps {
  customers: ArReportState['customers'];
  totals: ArReportState['totals'];
  isLoading: boolean;
  sortBy: GroupedSortBy;
  sortDir: 'asc' | 'desc';
  onSortToggle: (column: GroupedSortBy) => void;
  selectedCustomerIds: Set<string>;
  allSelected: boolean;
  someSelected: boolean;
  toggleSelectAll: () => void;
  toggleSelectOne: (id: string) => void;
  onCustomerClick: (customerId: string, customerName: string | null) => void;
}

function SortableHead({
  column,
  label,
  sortBy,
  sortDir,
  onSortToggle,
  className,
}: {
  column: GroupedSortBy;
  label: string;
  sortBy: GroupedSortBy;
  sortDir: 'asc' | 'desc';
  onSortToggle: (column: GroupedSortBy) => void;
  className?: string;
}) {
  return (
    <TableHead
      className={className}
      aria-sort={
        sortBy === column
          ? sortDir === 'asc'
            ? 'ascending'
            : 'descending'
          : undefined
      }
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-foreground ml-auto"
        onClick={() => onSortToggle(column)}
      >
        {label}
        {sortBy === column && sortDir === 'desc' ? (
          <CaretDownIcon className="h-3 w-3" />
        ) : sortBy === column && sortDir === 'asc' ? (
          <CaretUpIcon className="h-3 w-3" />
        ) : (
          <CaretDownIcon className="h-3 w-3 invisible" />
        )}
      </button>
    </TableHead>
  );
}

export function ArReportGroupedTable({
  customers,
  totals,
  isLoading,
  sortBy,
  sortDir,
  onSortToggle,
  selectedCustomerIds,
  allSelected,
  someSelected,
  toggleSelectAll,
  toggleSelectOne,
  onCustomerClick,
}: GroupedTableProps) {
  const gt = useGT();
  const customerIds = customers.map((c) => c.customerId);
  const { byCustomerId: commsByCustomerId, queryKey: commsQueryKey } =
    useArCustomerCommsSummary(customerIds);
  const setStatus = useSetCollectionStatus(
    commsQueryKey ? [commsQueryKey] : [],
  );
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={
                    allSelected && customers.length > 0
                      ? true
                      : someSelected
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={toggleSelectAll}
                  aria-label={gt('Select all customers')}
                />
              </TableHead>
              <SortableHead
                column="customerName"
                label={gt('Customer')}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortToggle={onSortToggle}
                className="min-w-[220px]"
              />
              <TableHead className="min-w-[140px]">
                <T>Status</T>
              </TableHead>
              <SortableHead
                column="openBalance"
                label={gt('Open Balance')}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortToggle={onSortToggle}
                className="text-right"
              />
              <SortableHead
                column="invoiceCount"
                label={gt('Invoices')}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortToggle={onSortToggle}
                className="text-right"
              />
              <SortableHead
                column="lastStatementSentAt"
                label={gt('Last Statement Sent')}
                sortBy={sortBy}
                sortDir={sortDir}
                onSortToggle={onSortToggle}
                className="text-right"
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell />
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24 ml-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12 ml-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24 ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="h-24 text-center text-muted-foreground"
                >
                  <T>No invoices match the selected filters</T>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => {
                const comms = commsByCustomerId.get(customer.customerId);
                const status: CustomerCollectionStatus =
                  // eslint-disable-next-line i18next/no-literal-string
                  comms?.collectionStatus ?? 'current';
                return (
                  <TableRow key={customer.customerId}>
                    <TableCell>
                      <Checkbox
                        checked={selectedCustomerIds.has(customer.customerId)}
                        onCheckedChange={() =>
                          toggleSelectOne(customer.customerId)
                        }
                        aria-label={gt('Select {customerName}', {
                          customerName: customer.customerName ?? gt('customer'),
                        })}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <CustomerCommsPopover
                        customerId={customer.customerId}
                        customerName={customer.customerName}
                        lastContactAt={comms?.lastContactAt ?? null}
                        recentActivity={comms?.recentActivity ?? []}
                        invalidateKeys={commsQueryKey ? [commsQueryKey] : []}
                      >
                        <button
                          type="button"
                          className="text-left text-blue-600 hover:underline cursor-pointer max-w-[300px] truncate block"
                          onClick={() =>
                            onCustomerClick(
                              customer.customerId,
                              customer.customerName,
                            )
                          }
                        >
                          {customer.customerName ?? gt('(Unknown)')}
                        </button>
                      </CustomerCommsPopover>
                    </TableCell>
                    <TableCell>
                      <CollectionStatusChip
                        status={status}
                        isPending={
                          setStatus.isPending &&
                          setStatus.variables?.customerId ===
                            customer.customerId
                        }
                        onChange={(next) =>
                          setStatus.mutate({
                            customerId: customer.customerId,
                            status: next,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatDecimalCurrency(customer.openBalance)}
                    </TableCell>
                    <TableCell className="text-right">
                      {customer.invoiceCount}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {customer.lastStatementSentAt
                        ? formatDate(customer.lastStatementSentAt.slice(0, 10))
                        : gt('Never', {
                            context:
                              'cell value when a customer has never been sent a statement',
                          })}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {!isLoading && totals && customers.length > 0 && (
            <TableFooter>
              <TableRow className="font-semibold bg-muted/50">
                <TableCell />
                <TableCell>
                  <T>Total</T>
                </TableCell>
                <TableCell />
                <TableCell className="text-right">
                  {formatDecimalCurrency(totals.openBalance)}
                </TableCell>
                <TableCell className="text-right">
                  {totals.invoiceCount}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
    </Card>
  );
}

// ─── Flat Table ──────────────────────────────────────────────────────────────

interface FlatTableProps {
  sortedInvoices: ArReportState['sortedInvoices'];
  isLoading: boolean;
  flatSortBy: ArInvoiceFlatSortBy;
  flatSortDir: 'asc' | 'desc';
  agingBasis: AgingBasis;
  onSortToggle: (column: ArInvoiceFlatSortBy) => void;
  onInvoiceClick: (invoiceId: string) => void;
}

export function ArReportFlatTable({
  sortedInvoices,
  isLoading,
  flatSortBy,
  flatSortDir,
  agingBasis,
  onSortToggle,
  onInvoiceClick,
}: FlatTableProps) {
  const gt = useGT();
  const uniqueCustomerIds = Array.from(
    new Set(sortedInvoices.map((inv) => inv.customerId).filter(Boolean)),
  );
  const { byCustomerId: commsByCustomerId, queryKey: commsQueryKey } =
    useArCustomerCommsSummary(uniqueCustomerIds);
  const setStatus = useSetCollectionStatus(
    commsQueryKey ? [commsQueryKey] : [],
  );
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              {(() => {
                type FlatCol = {
                  key: ArInvoiceFlatSortBy;
                  label: string;
                  align?: 'right';
                };
                /* eslint-disable i18next/no-literal-string */
                const columns: ReadonlyArray<FlatCol> = [
                  { key: 'invoiceNumber', label: gt('Invoice #') },
                  { key: 'customerName', label: gt('Customer') },
                  { key: 'firstSentAt', label: gt('Sent Date') },
                  { key: 'dueDate', label: gt('Due Date') },
                  { key: 'shipDate', label: gt('Ship Date') },
                  { key: 'daysOut', label: gt('Days Out'), align: 'right' },
                  { key: 'netAmount', label: gt('Amount'), align: 'right' },
                  {
                    key: 'appliedAmount',
                    label: gt('Applied'),
                    align: 'right',
                  },
                  { key: 'balanceDue', label: gt('Balance'), align: 'right' },
                  { key: 'status', label: gt('Status') },
                ];
                /* eslint-enable i18next/no-literal-string */
                return columns.map(({ key, label, align }) => (
                  <TableHead
                    key={key}
                    className={align === 'right' ? 'text-right' : ''}
                    aria-sort={
                      flatSortBy === key
                        ? flatSortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-foreground"
                      onClick={() => onSortToggle(key)}
                    >
                      {label}
                      {flatSortBy === key ? (
                        flatSortDir === 'desc' ? (
                          <CaretDownIcon className="h-3 w-3" />
                        ) : (
                          <CaretUpIcon className="h-3 w-3" />
                        )
                      ) : null}
                    </button>
                  </TableHead>
                ));
              })()}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`flat-skeleton-${i}`}>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12 ml-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20 ml-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20 ml-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20 ml-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                </TableRow>
              ))
            ) : sortedInvoices.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={11}
                  className="h-24 text-center text-muted-foreground"
                >
                  <T>No invoices match the selected filters</T>
                </TableCell>
              </TableRow>
            ) : (
              sortedInvoices.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onInvoiceClick(invoice.id)}
                >
                  <TableCell>
                    <span className="text-primary hover:underline text-sm font-medium">
                      {invoice.invoiceNumber ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell
                    className="text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(() => {
                      const comms = commsByCustomerId.get(invoice.customerId);
                      const status: CustomerCollectionStatus =
                        // eslint-disable-next-line i18next/no-literal-string
                        comms?.collectionStatus ?? 'current';
                      return (
                        <div className="flex items-center gap-2">
                          <CustomerCommsPopover
                            customerId={invoice.customerId}
                            customerName={invoice.customerName}
                            lastContactAt={comms?.lastContactAt ?? null}
                            recentActivity={comms?.recentActivity ?? []}
                            invalidateKeys={
                              commsQueryKey ? [commsQueryKey] : []
                            }
                          >
                            <span className="truncate max-w-[180px] cursor-help">
                              {invoice.customerName ?? '—'}
                            </span>
                          </CustomerCommsPopover>
                          <CollectionStatusChip
                            status={status}
                            isPending={
                              setStatus.isPending &&
                              setStatus.variables?.customerId ===
                                invoice.customerId
                            }
                            onChange={(next) =>
                              setStatus.mutate({
                                customerId: invoice.customerId,
                                status: next,
                              })
                            }
                          />
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm text-muted-foreground"
                  >
                    <InvoiceSentCell
                      firstSentAt={invoice.firstSentAt}
                      sendEvents={invoice.sendEvents}
                    />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(invoice.dueDate)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(invoice.shipDate)}
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {(() => {
                      const days = getDaysOut(invoice, agingBasis);
                      return days != null ? days : '—';
                    })()}
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold">
                    {formatDecimalCurrency(invoice.netAmount)}
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold">
                    {invoice.netAmount != null && invoice.balanceDue != null
                      ? formatDecimalCurrency(
                          String(
                            parseFloat(invoice.netAmount) -
                              parseFloat(invoice.balanceDue),
                          ),
                        )
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm font-semibold">
                    {formatDecimalCurrency(invoice.balanceDue)}
                  </TableCell>
                  <TableCell>{getInvoiceStatusBadge(invoice.status)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
